import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Configure REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY no arquivo .env')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// ---- AUTH ----
export const authService = {
  async signUp({ email, password, nome, instrumento }) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { nome, instrumento } }
    })
    if (error) throw error
    return data
  },

  async signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  onAuthChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// ---- PERFIL ----
export const profileService = {
  async getProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    if (error) throw error
    return data
  },

  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async uploadAvatar(userId, file) {
    const ext = file.name.split('.').pop()
    const path = `${userId}/avatar.${ext}`
    const { error } = await supabase.storage
      .from('cifras-arquivos')
      .upload(path, file, { upsert: true })
    if (error) throw error
    const { data } = supabase.storage.from('cifras-arquivos').getPublicUrl(path)
    return data.publicUrl
  }
}

// ---- GRUPOS ----
export const grupoService = {
  async criarGrupo({ nome, descricao, tipo, modulo = 'cifrasband' }, userId) {
    const { data: grupo, error } = await supabase
      .from('grupos')
      .insert({ nome, descricao, tipo, modulo, criado_por: userId })
      .select()
      .single()
    if (error) throw error

    // Adiciona criador como admin
    await supabase.from('grupo_membros').insert({
      grupo_id: grupo.id,
      user_id: userId,
      papel: 'admin'
    })
    return grupo
  },

  async entrarPorCodigo(codigo) {
    // Usa endpoint server-side para contornar o RLS da tabela grupos
    // (política "Membros veem seus grupos" impede que não-membros encontrem o grupo)
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) throw new Error('Sessão expirada. Faça login novamente.')

    const res = await fetch('/api/join-group', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ codigo }),
    })
    let json
    try { json = await res.json() } catch { json = {} }
    if (!res.ok) throw new Error(json.error || `Erro ${res.status} ao entrar no grupo`)
    return json.grupo
  },

  async meuGrupos(userId, modulo = null) {
    let query = supabase
      .from('grupo_membros')
      .select(`grupo_id, papel, grupos(*)`)
      .eq('user_id', userId)
    if (modulo) {
      query = query.eq('grupos.modulo', modulo)
    }
    const { data, error } = await query
    if (error) throw error
    return data.filter(d => d.grupos).map(d => ({ ...d.grupos, meu_papel: d.papel }))
  },

  async membrosDoGrupo(grupoId) {
    const { data, error } = await supabase
      .from('grupo_membros')
      .select(`*, profiles(id, nome, apelido, avatar_url, instrumento)`)
      .eq('grupo_id', grupoId)
    if (error) throw error
    return data
  }
}

// ---- CIFRAS ----
export const cifraService = {
  async listarCifras(grupoId, { pagina = 0, porPagina = 20 } = {}) {
    const { data, error, count } = await supabase
      .from('cifras')
      .select(`
        *,
        profiles(id, nome, apelido, avatar_url),
        curtidas(count),
        comentarios(count),
        favoritos(count)
      `, { count: 'exact' })
      .eq('grupo_id', grupoId)
      .order('publicado_em', { ascending: false })
      .range(pagina * porPagina, (pagina + 1) * porPagina - 1)
    if (error) throw error
    return { cifras: data, total: count }
  },

  async getCifra(id) {
    const { data, error } = await supabase
      .from('cifras')
      .select(`
        *,
        profiles(id, nome, apelido, avatar_url, instrumento),
        historico_tons(*, profiles(nome)),
        comentarios(*, profiles(nome, avatar_url))
      `)
      .eq('id', id)
      .single()
    if (error) throw error

    // Incrementa visualizações
    await supabase.from('cifras').update({ visualizacoes: (data.visualizacoes || 0) + 1 }).eq('id', id)
    return data
  },

  async postarCifra(dados) {
    const { data, error } = await supabase
      .from('cifras')
      .insert(dados)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async deletarCifra(cifraId, userId) {
    const { error } = await supabase
      .from('cifras')
      .delete()
      .eq('id', cifraId)
      .eq('autor_id', userId)
    return { error }
  },

  async uploadArquivo(userId, file, tipo) {
    const ext = file.name.split('.').pop()
    const nome = `${userId}/${Date.now()}.${ext}`
    const { error } = await supabase.storage
      .from('cifras-arquivos')
      .upload(nome, file)
    if (error) throw error
    const { data } = supabase.storage.from('cifras-arquivos').getPublicUrl(nome)
    return data.publicUrl
  },

  async curtir(cifraId, userId) {
    const { data: existente } = await supabase
      .from('curtidas')
      .select('id')
      .eq('cifra_id', cifraId)
      .eq('user_id', userId)
      .single()

    if (existente) {
      await supabase.from('curtidas').delete().eq('id', existente.id)
      return false
    } else {
      await supabase.from('curtidas').insert({ cifra_id: cifraId, user_id: userId })
      return true
    }
  },

  async favoritar(cifraId, userId) {
    const { data: existente } = await supabase
      .from('favoritos')
      .select('id')
      .eq('cifra_id', cifraId)
      .eq('user_id', userId)
      .single()

    if (existente) {
      await supabase.from('favoritos').delete().eq('id', existente.id)
      return false
    } else {
      await supabase.from('favoritos').insert({ cifra_id: cifraId, user_id: userId })
      return true
    }
  },

  async comentar(cifraId, autorId, texto) {
    const { data, error } = await supabase
      .from('comentarios')
      .insert({ cifra_id: cifraId, autor_id: autorId, texto })
      .select(`*, profiles(nome, avatar_url)`)
      .single()
    if (error) throw error
    return data
  },

  async registrarTom(cifraId, grupoId, userId, tom, observacao) {
    const { error } = await supabase
      .from('historico_tons')
      .insert({ cifra_id: cifraId, grupo_id: grupoId, user_id: userId, tom, observacao })
    if (error) throw error

    // Atualiza tom_ultima_vez na cifra
    await supabase.from('cifras').update({ tom_ultima_vez: tom }).eq('id', cifraId)
  },

  async salvarTomUltimaVez(cifraId, tom) {
    await supabase.from('cifras').update({ tom_ultima_vez: tom }).eq('id', cifraId)
  },

  async buscarCifras(grupoId, texto) {
    const { data, error } = await supabase
      .from('cifras')
      .select(`*, profiles(nome, avatar_url)`)
      .eq('grupo_id', grupoId)
      .or(`titulo.ilike.%${texto}%,artista.ilike.%${texto}%,letra_trecho.ilike.%${texto}%`)
      .limit(20)
    if (error) throw error
    return data
  }
}

// ============================================================
// SETLISTS - Pastas por data/culto
// ============================================================
export const setlistService = {
  async listar(grupoId) {
    const { data, error } = await supabase
      .from('setlists')
      .select(`*, profiles(nome), setlist_cifras(count)`)
      .eq('grupo_id', grupoId)
      .order('data_evento', { ascending: false })
    if (error) throw error
    return data
  },

  async criar(dados) {
    const { data, error } = await supabase
      .from('setlists')
      .insert(dados)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async getCifras(setlistId) {
    const { data, error } = await supabase
      .from('setlist_cifras')
      .select(`*, cifras(*, profiles(nome, avatar_url))`)
      .eq('setlist_id', setlistId)
      .order('ordem')
    if (error) throw error
    return data
  },

  async adicionarCifra(setlistId, cifraId, tom, ordem = 0) {
    const { error } = await supabase
      .from('setlist_cifras')
      .upsert({ setlist_id: setlistId, cifra_id: cifraId, tom_usado: tom, ordem })
    if (error) throw error
  },

  async removerCifra(setlistId, cifraId) {
    const { error } = await supabase
      .from('setlist_cifras')
      .delete()
      .eq('setlist_id', setlistId)
      .eq('cifra_id', cifraId)
    if (error) throw error
  },

  async deletar(setlistId) {
    const { error } = await supabase.from('setlists').delete().eq('id', setlistId)
    if (error) throw error
  },

  async reordenar(setlistId, cifraId, novaOrdem) {
    const { error } = await supabase
      .from('setlist_cifras')
      .update({ ordem: novaOrdem })
      .eq('setlist_id', setlistId)
      .eq('cifra_id', cifraId)
    if (error) throw error
  },

  async atualizarCor(setlistId, cor) {
    const { error } = await supabase
      .from('setlists')
      .update({ cor })
      .eq('id', setlistId)
    if (error) throw error
  }
}

// ============================================================
// GERENCIAMENTO DE MEMBROS
// ============================================================
export const membroService = {
  // Admin remove um membro do grupo
  async removerMembro(grupoId, userId, adminId) {
    // Verifica se quem remove é admin
    const { data: admin } = await supabase
      .from('grupo_membros')
      .select('papel')
      .eq('grupo_id', grupoId)
      .eq('user_id', adminId)
      .single()
    if (admin?.papel !== 'admin') throw new Error('Apenas admins podem remover membros')

    const { error } = await supabase
      .from('grupo_membros')
      .delete()
      .eq('grupo_id', grupoId)
      .eq('user_id', userId)
    if (error) throw error
  },

  // Membro sai voluntariamente do grupo
  async sairDoGrupo(grupoId, userId) {
    // Verifica se é o único admin — não pode sair
    const { data: membros } = await supabase
      .from('grupo_membros')
      .select('user_id, papel')
      .eq('grupo_id', grupoId)

    const admins = membros?.filter(m => m.papel === 'admin') || []
    const euSouAdmin = admins.some(a => a.user_id === userId)

    if (euSouAdmin && admins.length === 1 && membros.length > 1) {
      throw new Error('Você é o único admin. Promova outro membro antes de sair.')
    }

    const { error } = await supabase
      .from('grupo_membros')
      .delete()
      .eq('grupo_id', grupoId)
      .eq('user_id', userId)
    if (error) throw error
  },

  // Admin promove membro para admin
  async promoverAdmin(grupoId, userId) {
    const { error } = await supabase
      .from('grupo_membros')
      .update({ papel: 'admin' })
      .eq('grupo_id', grupoId)
      .eq('user_id', userId)
    if (error) throw error
  },

  // Admin concede/revoga permissão de iniciar show
  async togglePodeShow(grupoId, userId, podeShow) {
    const { error } = await supabase
      .from('grupo_membros')
      .update({ pode_show: podeShow })
      .eq('grupo_id', grupoId)
      .eq('user_id', userId)
    if (error) throw error
  }
}
